import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft, RefreshCw, Loader2, Plug, CheckCircle2, AlertCircle,
  PlayCircle, ShieldCheck, Trash2, Webhook, Zap, X, Users,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Mindbody Integration — Admin-Settings + Sync-Status.
 *
 * Phase 1: Read-Only Mirror. Mindbody-Slots erscheinen im StrongFit-Kalender
 * als graue "BELEGT"-Blöcke (nicht buchbar), damit sich die Buchungen während
 * der Migration nicht überlagern.
 *
 * Deaktivierung: einfach den "Aktiv"-Toggle ausschalten. Der Scheduler läuft
 * weiterhin alle 15 Min, macht aber no-op solange active=false.
 */
const MindbodyIntegrationPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [purging, setPurging] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawPreview, setWithdrawPreview] = useState(null);
  const [withdrawJob, setWithdrawJob] = useState(null);
  // Iter 339+ Orphan-Cleanup state
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanResult, setOrphanResult] = useState(null);
  const [orphanDeleting, setOrphanDeleting] = useState(false);
  const [orphanExtraIds, setOrphanExtraIds] = useState('');
  const [orphanTargetIds, setOrphanTargetIds] = useState('');
  const [orphanDaysBack, setOrphanDaysBack] = useState(0);
  const [orphanConfirm, setOrphanConfirm] = useState('');
  const [webhooks, setWebhooks] = useState([]);
  const [staffMappings, setStaffMappings] = useState([]);
  const [availableTrainers, setAvailableTrainers] = useState([]);
  const [serviceMappings, setServiceMappings] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [sessionTypesError, setSessionTypesError] = useState(null);
  const [registeringWebhook, setRegisteringWebhook] = useState(false);
  const [repairingWebhooks, setRepairingWebhooks] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    api_key: '',
    site_id: '',
    source_name: '',
    source_password: '',
    sync_direction: 'off',
    push_session_type_id: '',
    push_default_client_id: '',
    push_extra_clients: [],
  });
  // Dummy-Client-Suche (06/2026): Zusatz-Clients für 1:2/1:3-Gruppen
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  useEffect(() => {
    fetchSettings();
    fetchWebhooks();
    fetchStaffMappings();
    fetchServiceMappings();
    // Falls ein Withdrawal-Job bereits läuft → Fortschritt weiter anzeigen.
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/mindbody/withdraw-strongfit-data/status`, authHeader());
        const job = res.data?.job;
        if (job && (job.status === 'queued' || job.status === 'running')) {
          setWithdrawJob(job);
          pollWithdrawStatus();
        }
      } catch (e) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/mindbody/settings`, authHeader());
      setSettings(res.data);
      setForm((prev) => ({
        ...prev,
        site_id: res.data.site_id || '',
        source_name: res.data.source_name || '',
        sync_direction: res.data.sync_direction || 'off',
        push_session_type_id: res.data.push_session_type_id || '',
        push_default_client_id: res.data.push_default_client_id || '',
        push_extra_clients: res.data.push_extra_clients || [],
        api_key: '',
        source_password: '',
      }));
    } catch (e) {
      toast.error('Konnte Mindbody-Einstellungen nicht laden');
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const res = await axios.get(`${API}/admin/mindbody/webhooks`, authHeader());
      setWebhooks(res.data?.subscriptions || []);
    } catch (e) {
      // silently fail — webhooks sind optional
    }
  };

  const fetchStaffMappings = async () => {
    try {
      const res = await axios.get(`${API}/admin/mindbody/staff-mappings`, authHeader());
      setStaffMappings(res.data?.mappings || []);
      setAvailableTrainers(res.data?.available_trainers || []);
    } catch (e) {
      // silently fail
    }
  };

  const setStaffMapping = async (mindbodyStaffId, strongfitTrainerId) => {
    try {
      await axios.put(
        `${API}/admin/mindbody/staff-mappings/${encodeURIComponent(mindbodyStaffId)}`,
        { strongfit_trainer_id: strongfitTrainerId || null },
        authHeader(),
      );
      toast.success(strongfitTrainerId ? 'Zuordnung gespeichert' : 'Zuordnung entfernt');
      await fetchStaffMappings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zuordnung fehlgeschlagen');
    }
  };

  // Service-Mapping (06/2026): StrongFit-Dienstleistung ↔ Mindbody-Session-Type
  const fetchServiceMappings = async () => {
    try {
      const res = await axios.get(`${API}/admin/mindbody/service-mappings`, authHeader());
      setServiceMappings(res.data?.mappings || []);
      setSessionTypes(res.data?.session_types || []);
      setSessionTypesError(res.data?.session_types_error || null);
    } catch (e) {
      // silently fail
    }
  };

  const setServiceMapping = async (serviceId, sessionTypeId) => {
    try {
      const st = sessionTypes.find((t) => t.id === sessionTypeId);
      await axios.put(
        `${API}/admin/mindbody/service-mappings/${encodeURIComponent(serviceId)}`,
        {
          mindbody_session_type_id: sessionTypeId || null,
          mindbody_session_type_name: st?.name || null,
        },
        authHeader(),
      );
      toast.success(sessionTypeId ? 'Service-Zuordnung gespeichert' : 'Service-Zuordnung entfernt');
      await fetchServiceMappings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Service-Zuordnung fehlgeschlagen');
    }
  };

  const registerWebhook = async () => {
    setRegisteringWebhook(true);
    try {
      const res = await axios.post(`${API}/admin/mindbody/webhooks`, {}, authHeader());
      toast.success(`Webhook registriert: ${res.data?.subscription?.subscription_id}`);
      await fetchWebhooks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Webhook-Registrierung fehlgeschlagen');
    } finally {
      setRegisteringWebhook(false);
    }
  };

  const repairWebhooks = async () => {
    setRepairingWebhooks(true);
    try {
      const res = await axios.post(`${API}/admin/mindbody/webhooks/repair`, {}, authHeader());
      const { reactivated = [], deleted_orphans = [] } = res.data || {};
      if (reactivated.length > 0) {
        toast.success(`${reactivated.length} Subscription(s) reaktiviert ✓`);
      } else {
        toast.success('Alle Subscriptions bereits aktiv ✓');
      }
      if (deleted_orphans.length > 0) {
        toast.info(`${deleted_orphans.length} verwaiste Duplikate bei Mindbody gelöscht`);
      }
      await fetchWebhooks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Webhook-Reparatur fehlgeschlagen');
    } finally {
      setRepairingWebhooks(false);
    }
  };

  const activateWebhook = async (subscriptionId) => {
    try {
      const res = await axios.post(`${API}/admin/mindbody/webhooks/${subscriptionId}/activate`, {}, authHeader());
      toast.success(`Webhook aktiviert (Status: ${res.data?.status})`);
      await fetchWebhooks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aktivierung fehlgeschlagen');
    }
  };

  const deleteWebhook = async (subscriptionId) => {
    if (!window.confirm('Webhook-Subscription wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/admin/mindbody/webhooks/${subscriptionId}`, authHeader());
      toast.success('Webhook gelöscht');
      await fetchWebhooks();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const searchClients = async () => {
    if (!clientSearch || clientSearch.trim().length < 2) {
      toast.error('Mindestens 2 Zeichen eingeben');
      return;
    }
    setSearchingClients(true);
    try {
      const res = await axios.get(`${API}/admin/mindbody/clients/search`, {
        ...authHeader(),
        params: { q: clientSearch.trim() },
      });
      setClientResults(res.data?.clients || []);
      if ((res.data?.clients || []).length === 0) toast.info('Keine Mindbody-Clients gefunden');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Client-Suche fehlgeschlagen');
    } finally {
      setSearchingClients(false);
    }
  };

  const addExtraClient = async (client) => {
    if (form.push_extra_clients.some((c) => c.id === client.id)) {
      toast.error('Client ist bereits zugeordnet');
      return;
    }
    if (client.id === String(form.push_default_client_id)) {
      toast.error('Dieser Client ist bereits der Standard-Client');
      return;
    }
    const newList = [
      ...form.push_extra_clients,
      { id: client.id, name: `${client.first_name} ${client.last_name}`.trim() },
    ];
    setForm((prev) => ({ ...prev, push_extra_clients: newList }));
    setClientResults([]);
    setClientSearch('');
    await save({ push_extra_clients: newList });
  };

  const removeExtraClient = async (clientId) => {
    const newList = form.push_extra_clients.filter((c) => c.id !== clientId);
    setForm((prev) => ({ ...prev, push_extra_clients: newList }));
    await save({ push_extra_clients: newList });
  };


  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = { ...form, ...overrides };
      // Leere Felder nicht überschreiben
      if (!payload.api_key) delete payload.api_key;
      if (!payload.source_password) delete payload.source_password;
      if (!payload.site_id) delete payload.site_id;
      else payload.site_id = parseInt(payload.site_id, 10);
      if (!payload.source_name) delete payload.source_name;
      if (payload.push_session_type_id === '' || payload.push_session_type_id == null)
        delete payload.push_session_type_id;
      else payload.push_session_type_id = parseInt(payload.push_session_type_id, 10);
      if (!payload.push_default_client_id) delete payload.push_default_client_id;
      // Active nicht extra senden — wird aus sync_direction abgeleitet
      delete payload.active;

      const res = await axios.put(`${API}/admin/mindbody/settings`, payload, authHeader());
      setSettings(res.data);
      setForm((prev) => ({
        ...prev,
        api_key: '',
        source_password: '',
        sync_direction: res.data.sync_direction || 'off',
      }));
      toast.success('Mindbody-Einstellungen gespeichert');
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const changeDirection = async (newDirection) => {
    const previousDirection = settings?.sync_direction || 'off';
    // Wechsel zu OFF → Warnung
    if (newDirection === 'off' && previousDirection !== 'off') {
      const ok = window.confirm(
        'Sync wirklich auf "Aus" stellen?\n\n' +
        '• Alle gespiegelten Mindbody-Slots werden aus dem Kalender entfernt.\n' +
        '• Termine, die du zu Mindbody gepusht hast, BLEIBEN dort (musst du manuell in Mindbody aufräumen).\n\n' +
        'Es wird ein Audit-Eintrag geschrieben.'
      );
      if (!ok) {
        setForm((prev) => ({ ...prev, sync_direction: previousDirection }));
        return;
      }
    }
    setForm((prev) => ({ ...prev, sync_direction: newDirection }));
    await save({ sync_direction: newDirection });
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/mindbody/test-connection`, {}, authHeader());
      if (res.data?.success) {
        toast.success(`Verbindung OK (Site ${res.data.site_id})`);
      } else {
        toast.error(`Verbindung fehlgeschlagen: ${res.data?.error || 'unbekannt'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/mindbody/sync-now`, {}, authHeader());
      if (res.data?.success) {
        toast.success(
          `Sync OK: ${res.data.classes_fetched} Classes, ${res.data.appointments_fetched} Termine, ${res.data.synced} gespeichert, ${res.data.deleted} entfernt.`
        );
      } else {
        toast.error(`Sync-Fehler: ${res.data?.error || res.data?.reason || 'unbekannt'}`);
      }
      await fetchSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const purgeSlots = async () => {
    if (!window.confirm('Wirklich ALLE gespiegelten Mindbody-Slots aus der DB löschen?')) return;
    setPurging(true);
    try {
      const res = await axios.delete(`${API}/admin/mindbody/slots`, authHeader());
      toast.success(`${res.data?.deleted || 0} Slots gelöscht`);
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setPurging(false);
    }
  };

  // StrongFit-Daten aus Mindbody zurückziehen (vor finaler Abkopplung)
  // Schritt 1: Dry-Run laden → Schritt 2: Bestätigen → Schritt 3: Ausführen
  const fetchWithdrawPreview = async () => {
    setWithdrawing(true);
    try {
      const res = await axios.post(
        `${API}/admin/mindbody/withdraw-strongfit-data?dry_run=true`,
        null,
        authHeader(),
      );
      setWithdrawPreview(res.data);
    } catch (e) {
      toast.error('Vorschau fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setWithdrawing(false);
    }
  };

  const executeWithdraw = async () => {
    const t = withdrawPreview?.totals || {};
    const sum = (t.appointments || 0) + (t.extras || 0) + (t.unavailabilities || 0);
    if (sum === 0) {
      toast.info('Nichts zu löschen — keine StrongFit-Spuren in Mindbody gefunden.');
      setWithdrawPreview(null);
      return;
    }
    if (!window.confirm(
      `🧹 ENDGÜLTIG ${sum} StrongFit-Einträge aus Mindbody zurückziehen?\n\n` +
      `• ${t.appointments || 0} Termine (Dummy-Clients)\n` +
      `• ${t.extras || 0} Gruppen-Zusatz-Buchungen\n` +
      `• ${t.unavailabilities || 0} Personalverhinderungen\n\n` +
      `NATIVE Mindbody-Buchungen bleiben UNBERÜHRT.\nDiese Aktion kann nicht rückgängig gemacht werden.`,
    )) return;

    setWithdrawing(true);
    try {
      const res = await axios.post(
        `${API}/admin/mindbody/withdraw-strongfit-data?dry_run=false`,
        null,
        authHeader(),
      );
      if (res.data?.already_running) {
        toast.info('Ein Zurückziehen läuft bereits.');
        setWithdrawJob(res.data.job);
        pollWithdrawStatus();
        return;
      }
      // Hintergrund-Job gestartet → Fortschritt pollen (kein 524-Timeout mehr).
      toast.info('Zurückziehen gestartet – das läuft jetzt im Hintergrund.');
      setWithdrawPreview(null);
      setWithdrawJob({ status: 'queued', total: 0, processed: 0, success_count: 0, error_count: 0 });
      pollWithdrawStatus();
    } catch (e) {
      toast.error('Zurückziehen fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setWithdrawing(false);
    }
  };

  // Pollt den Fortschritt des Hintergrund-Jobs bis "done"/"error".
  const pollWithdrawStatus = async () => {
    let done = false;
    while (!done) {
      try {
        const res = await axios.get(
          `${API}/admin/mindbody/withdraw-strongfit-data/status`, authHeader(),
        );
        const job = res.data?.job;
        if (job) {
          setWithdrawJob(job);
          if (job.status === 'done' || job.status === 'error') {
            done = true;
            if (job.status === 'done') {
              const err = job.error_count || 0;
              if (err === 0) {
                toast.success(`✅ ${job.success_count || 0} StrongFit-Einträge aus Mindbody zurückgezogen.`);
              } else {
                toast.warning(`⚠️ ${job.success_count || 0} erfolgreich, ${err} Fehler.`);
              }
            } else {
              toast.error('Zurückziehen fehlgeschlagen: ' + (job.error || 'unbekannter Fehler'));
            }
          }
        }
      } catch (e) {
        done = true;
      }
      if (!done) {
        await new Promise((r) => setTimeout(r, 3000));
      }
    }
  };

  // Iter 339+ Orphan Cleanup handlers
  const fetchOrphanPreview = async () => {
    setOrphanLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ days_ahead: '365' });
      if (orphanDaysBack > 0) params.set('days_back', String(orphanDaysBack));
      if (orphanExtraIds.trim()) params.set('extra_client_ids', orphanExtraIds.trim());
      if (orphanTargetIds.trim()) params.set('target_client_ids', orphanTargetIds.trim());
      const res = await axios.get(`${API}/admin/mindbody/orphans?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrphanResult(res.data);
      if (res.data?.error) {
        toast.error(res.data.error);
      } else if (res.data?.orphan_count === 0) {
        toast.info('Keine verwaisten Termine gefunden — Mindbody ist sauber.');
      } else {
        toast.success(`${res.data.orphan_count} verwaiste Termine gefunden.`);
      }
    } catch (e) {
      toast.error('Vorschau fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setOrphanLoading(false);
    }
  };

  const executeOrphanDelete = async () => {
    if (orphanConfirm !== 'DELETE-ORPHANS') {
      toast.error('Bitte „DELETE-ORPHANS" exakt eintippen zur Bestätigung.');
      return;
    }
    if (!orphanResult?.orphans?.length) return;
    setOrphanDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/mindbody/orphans/delete`,
        {
          mindbody_ids: orphanResult.orphans.map(o => o.mindbody_id),
          confirm: 'DELETE-ORPHANS',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const r = res.data || {};
      if ((r.errors?.length || 0) === 0) {
        toast.success(`✅ ${r.deleted} verwaiste Termine erfolgreich in Mindbody storniert.`);
      } else {
        toast.warning(`${r.deleted} ok, ${r.errors.length} Fehler. Konsole prüfen.`);
        console.warn('Orphan-Delete-Errors:', r.errors);
      }
      setOrphanResult(null);
      setOrphanConfirm('');
    } catch (e) {
      toast.error('Löschen fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setOrphanDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin h-8 w-8 text-yellow-500" />
      </div>
    );
  }

  const lastStats = settings?.last_sync_stats;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="mindbody-integration-page">
      <div className="flex items-center justify-between">
        <div>
          {!embedded && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            data-testid="mindbody-back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Zurück
          </Button>
          )}
          <h1 className="text-3xl font-bold mt-2 flex items-center gap-2">
            <Plug className="h-7 w-7 text-yellow-500" /> Mindbody Integration
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Phase 1 — Read-Only Mirror. Mindbody-Termine erscheinen im StrongFit-Kalender
            als <strong>BELEGT</strong>, damit es während der Migration zu keiner
            Doppelbuchung kommt.
          </p>
        </div>
        <Badge
          variant={settings?.active ? 'default' : 'secondary'}
          className={settings?.active ? 'bg-green-600' : ''}
          data-testid="mindbody-status-badge"
        >
          {settings?.sync_direction === 'mb_to_sf' && 'Mindbody → StrongFit'}
          {settings?.sync_direction === 'sf_to_mb' && 'StrongFit → Mindbody'}
          {settings?.sync_direction === 'both' && 'Beide Richtungen'}
          {(!settings?.sync_direction || settings?.sync_direction === 'off') && 'Aus'}
        </Badge>
      </div>

      {/* Status Card */}
      <Card data-testid="mindbody-status-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Sync-Status
          </CardTitle>
          <CardDescription>
            Cron läuft alle 15 Minuten, holt die nächsten 60 Tage. Bei <em>Inaktiv</em>{' '}
            macht der Cron nichts (no-op).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Letzte Synchronisation</Label>
              <p data-testid="mindbody-last-sync">
                {settings?.last_sync_at
                  ? new Date(settings.last_sync_at).toLocaleString('de-CH')
                  : 'noch nie'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <p className="flex items-center gap-2">
                {settings?.last_sync_error ? (
                  <>
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <span className="text-red-500">Fehler</span>
                  </>
                ) : settings?.last_sync_at ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>OK</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </p>
            </div>
          </div>

          {settings?.last_sync_error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800 break-all">
              {settings.last_sync_error}
            </div>
          )}

          {lastStats && !settings?.last_sync_error && (
            <div className="grid grid-cols-4 gap-2 pt-2">
              <div>
                <div className="text-2xl font-bold">{lastStats.classes_fetched ?? 0}</div>
                <div className="text-xs text-muted-foreground">Classes</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{lastStats.appointments_fetched ?? 0}</div>
                <div className="text-xs text-muted-foreground">Termine</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{lastStats.synced ?? 0}</div>
                <div className="text-xs text-muted-foreground">gespeichert</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{lastStats.deleted ?? 0}</div>
                <div className="text-xs text-muted-foreground">entfernt</div>
              </div>
            </div>
          )}

          <Separator />

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={syncNow}
              disabled={syncing || !settings?.api_key_set}
              data-testid="mindbody-sync-now-btn"
            >
              {syncing ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <PlayCircle className="h-4 w-4 mr-2" />}
              Jetzt synchronisieren
            </Button>
            <Button
              variant="outline"
              onClick={testConnection}
              disabled={testing || !settings?.api_key_set}
              data-testid="mindbody-test-connection-btn"
            >
              {testing ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Verbindung testen
            </Button>
            <Button
              variant="destructive"
              onClick={purgeSlots}
              disabled={purging}
              data-testid="mindbody-purge-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Alle Spiegel-Daten löschen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Withdrawal Card — StrongFit-Daten aus Mindbody zurückziehen
          Zweck: Vor finaler Abkopplung von Mindbody alle StrongFit-Spuren
          dort sauber entfernen (Dummy-Client-Termine + alte Unavail-Blocks).
          Native Mindbody-Buchungen bleiben unberührt. */}
      <Card data-testid="mindbody-withdraw-card" className="border-red-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" /> StrongFit-Daten aus Mindbody zurückziehen
          </CardTitle>
          <CardDescription>
            Vor der finalen Abkopplung: löscht in Mindbody alle Termine &amp; Blocks, die StrongFit jemals
            dorthin gepusht hat (Dummy-Client-Termine + alte Personalverhinderungen).
            <strong className="block mt-1 text-red-700">Native Mindbody-Buchungen (echte Kunden-Termine) bleiben unberührt.</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {withdrawJob && (withdrawJob.status === 'queued' || withdrawJob.status === 'running') && (
            <div className="rounded border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="mindbody-withdraw-progress">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                <Loader2 className="animate-spin h-4 w-4" />
                Zurückziehen läuft im Hintergrund…
              </div>
              <div className="w-full bg-amber-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-amber-600 h-2.5 rounded-full transition-all"
                  style={{ width: `${withdrawJob.total ? Math.round((withdrawJob.processed / withdrawJob.total) * 100) : 0}%` }}
                />
              </div>
              <div className="text-xs text-amber-800">
                {withdrawJob.processed || 0} / {withdrawJob.total || 0} verarbeitet
                · {withdrawJob.success_count || 0} erfolgreich · {withdrawJob.error_count || 0} Fehler
              </div>
              <p className="text-xs text-amber-700">Du kannst die Seite verlassen – der Vorgang läuft weiter.</p>
            </div>
          )}
          {withdrawJob && withdrawJob.status === 'done' && (
            <div className="rounded border border-green-300 bg-green-50 p-4 text-sm text-green-800" data-testid="mindbody-withdraw-done">
              ✅ Abgeschlossen: {withdrawJob.success_count || 0} zurückgezogen
              {withdrawJob.error_count ? `, ${withdrawJob.error_count} Fehler` : ''}.
              <Button variant="ghost" size="sm" className="ml-2" onClick={() => setWithdrawJob(null)}>Ausblenden</Button>
            </div>
          )}
          {(!withdrawJob || withdrawJob.status === 'done' || withdrawJob.status === 'error') && (!withdrawPreview ? (
            <Button
              variant="outline"
              onClick={fetchWithdrawPreview}
              disabled={withdrawing || !settings?.api_key_set}
              data-testid="mindbody-withdraw-preview-btn"
            >
              {withdrawing ? (
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
              ) : (
                <ShieldCheck className="h-4 w-4 mr-2" />
              )}
              Vorschau anzeigen (kein Eingriff)
            </Button>
          ) : (
            <div className="rounded border border-red-200 bg-red-50 p-4 space-y-3" data-testid="mindbody-withdraw-preview">
              <div className="text-sm font-semibold text-red-800">
                {(withdrawPreview.totals?.appointments || 0) +
                  (withdrawPreview.totals?.extras || 0) +
                  (withdrawPreview.totals?.unavailabilities || 0) === 0 ? (
                  <span className="text-green-700">
                    ✅ Nichts zu löschen — keine StrongFit-Spuren in Mindbody gefunden.
                  </span>
                ) : (
                  <>Es würden zurückgezogen werden:</>
                )}
              </div>
              <ul className="text-sm text-red-900 list-disc ml-5 space-y-1">
                <li>{withdrawPreview.totals?.appointments || 0} Termine (Haupt-Buchungen via Dummy-Client)</li>
                <li>{withdrawPreview.totals?.extras || 0} Gruppen-Zusatz-Buchungen (1:2 / 1:3 Teilnehmer)</li>
                <li>{withdrawPreview.totals?.unavailabilities || 0} Personalverhinderungen (alte Push-Methode)</li>
              </ul>

              {(withdrawPreview.appointments?.length || 0) > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-red-700 font-medium">
                    Termine-Details ({withdrawPreview.appointments.length})
                  </summary>
                  <div className="mt-2 max-h-48 overflow-auto space-y-1 bg-white rounded border p-2">
                    {withdrawPreview.appointments.map((a) => (
                      <div key={a.sf_id} className="text-xs">
                        <code className="text-red-700">MB#{a.mindbody_pushed_id}</code>{' '}
                        {a.start_time} — {a.service_name || 'Termin'}
                        {a.extras?.length > 0 && (
                          <span className="text-gray-500"> (+{a.extras.length} Zusatz)</span>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {(withdrawPreview.unavailabilities?.length || 0) > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-red-700 font-medium">
                    Verhinderungen-Details ({withdrawPreview.unavailabilities.length})
                  </summary>
                  <div className="mt-2 max-h-48 overflow-auto space-y-1 bg-white rounded border p-2">
                    {withdrawPreview.unavailabilities.map((u) => (
                      <div key={u.sf_id} className="text-xs">
                        <code className="text-red-700">MB#{u.mindbody_pushed_unavailability_id}</code>{' '}
                        {u.start_time}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="destructive"
                  onClick={executeWithdraw}
                  disabled={withdrawing}
                  data-testid="mindbody-withdraw-execute-btn"
                >
                  {withdrawing ? (
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Jetzt endgültig zurückziehen
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setWithdrawPreview(null)}
                  disabled={withdrawing}
                  data-testid="mindbody-withdraw-cancel-btn"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Iter 339+ Mindbody-Geister-Termine Cleanup */}
      <Card data-testid="mindbody-orphans-card" className="border-orange-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700">
            👻 Verwaiste Mindbody-Termine aufräumen
          </CardTitle>
          <CardDescription>
            Findet Termine die in Mindbody auf einem StrongFit-Dummy-Client liegen,
            in StrongFit aber kein Pendant (mehr) haben — entsteht z.B. durch alte
            Serien-Termine oder Legacy-Dummy-Clients. <strong>Nur zukünftige Termine
            werden geprüft.</strong> Echte Mindbody-Kunden-Buchungen werden niemals
            angerührt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label className="text-sm">
              Zusätzliche Legacy-Dummy-Client-IDs (optional, kommagetrennt)
            </Label>
            <Input
              placeholder="z.B. 100000807, 100000900"
              value={orphanExtraIds}
              onChange={(e) => setOrphanExtraIds(e.target.value)}
              data-testid="orphan-extra-ids-input"
            />
            <p className="text-xs text-gray-500">
              Falls du einen alten Dummy-Client hattest, der nicht mehr in den
              Mindbody-Settings steht aber noch Geister-Termine produziert hat.
              Default-Dummies aus den Settings werden automatisch berücksichtigt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm">
                🎯 Gezielte Client-ID(s) prüfen (Diagnose, kommagetrennt)
              </Label>
              <Input
                placeholder="z.B. 100000817"
                value={orphanTargetIds}
                onChange={(e) => setOrphanTargetIds(e.target.value)}
                data-testid="orphan-target-ids-input"
              />
              <p className="text-xs text-gray-500">
                Schränkt die Suche AUSSCHLIESSLICH auf diese ID(s) ein —
                ideal um zu testen ob ein bestimmter Dummy Termine hat.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">
                ⏪ Vergangenheit mitprüfen (Tage zurück)
              </Label>
              <Input
                type="number"
                min="0"
                max="1825"
                placeholder="0 = nur Zukunft"
                value={orphanDaysBack}
                onChange={(e) => setOrphanDaysBack(parseInt(e.target.value) || 0)}
                data-testid="orphan-days-back-input"
              />
              <p className="text-xs text-gray-500">
                0 = nur zukünftige Termine. 365 = ein Jahr zurück.
                Mehr = langsamer aber gründlicher.
              </p>
            </div>
          </div>

          {!orphanResult && (
            <Button
              variant="outline"
              onClick={fetchOrphanPreview}
              disabled={orphanLoading || !settings?.api_key_set}
              data-testid="orphan-preview-btn"
            >
              {orphanLoading ? (
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
              ) : (
                <ShieldCheck className="h-4 w-4 mr-2" />
              )}
              Vorschau anzeigen (kein Eingriff)
            </Button>
          )}

          {orphanResult && (
            <div className="rounded border border-orange-200 bg-orange-50 p-4 space-y-3" data-testid="orphan-result">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-white rounded p-2">
                  <p className="text-gray-500">Geprüft</p>
                  <p className="text-xl font-bold">{orphanResult.checked_count}</p>
                </div>
                <div className="bg-green-100 rounded p-2">
                  <p className="text-green-700">Valider Sync</p>
                  <p className="text-xl font-bold text-green-800">{orphanResult.valid_count}</p>
                </div>
                <div className="bg-red-100 rounded p-2">
                  <p className="text-red-700">👻 Verwaist</p>
                  <p className="text-xl font-bold text-red-800">{orphanResult.orphan_count}</p>
                </div>
              </div>

              <details className="text-xs">
                <summary className="cursor-pointer text-orange-700 font-medium">
                  Genutzte Dummy-IDs ({(orphanResult.pool_used || []).length})
                </summary>
                <div className="mt-2 text-gray-700 font-mono">
                  {(orphanResult.pool_used || []).join(', ') || '—'}
                </div>
              </details>

              {Object.keys(orphanResult.client_breakdown || {}).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-orange-700 font-medium">
                    Top Client-IDs im Mindbody-Zeitraum
                  </summary>
                  <div className="mt-2 max-h-32 overflow-auto bg-white rounded border p-2 font-mono space-y-0.5">
                    {Object.entries(orphanResult.client_breakdown || {}).slice(0, 20).map(([cid, count]) => (
                      <div key={cid} className={(orphanResult.pool_used || []).includes(cid) ? 'text-blue-700' : 'text-gray-700'}>
                        {cid} → {count} Termine {(orphanResult.pool_used || []).includes(cid) && '(Dummy)'}
                      </div>
                    ))}
                  </div>
                  <p className="mt-1 text-gray-500">
                    Klingelt eine ID? Trage sie oben in &bdquo;Legacy-Dummy-Client-IDs&ldquo; ein
                    und klick erneut auf Vorschau.
                  </p>
                </details>
              )}

              {orphanResult.orphan_count > 0 && (
                <>
                  <details className="text-xs" open>
                    <summary className="cursor-pointer text-red-700 font-medium">
                      Verwaiste Termine ({orphanResult.orphan_count})
                    </summary>
                    <div className="mt-2 max-h-64 overflow-auto bg-white rounded border p-2 space-y-1">
                      {orphanResult.orphans.map((o) => (
                        <div key={o.mindbody_id} className="text-xs border-b border-gray-100 pb-1">
                          <code className="text-red-700">MB#{o.mindbody_id}</code>{' '}
                          <span className="text-gray-700">{o.start_time}</span>{' '}
                          <span className="text-gray-500">→ {o.trainer_name || '—'}</span>{' '}
                          {o.service_name && <span className="text-gray-500">· {o.service_name}</span>}
                        </div>
                      ))}
                    </div>
                  </details>

                  <div className="border-t border-orange-300 pt-3 space-y-2">
                    <Label className="text-sm font-semibold text-red-800">
                      Bestätigung: &bdquo;DELETE-ORPHANS&ldquo; eintippen
                    </Label>
                    <Input
                      placeholder="DELETE-ORPHANS"
                      value={orphanConfirm}
                      onChange={(e) => setOrphanConfirm(e.target.value)}
                      data-testid="orphan-confirm-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        onClick={executeOrphanDelete}
                        disabled={orphanDeleting || orphanConfirm !== 'DELETE-ORPHANS'}
                        data-testid="orphan-delete-btn"
                      >
                        {orphanDeleting ? (
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-2" />
                        )}
                        {orphanResult.orphan_count} verwaiste Termine löschen
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => { setOrphanResult(null); setOrphanConfirm(''); }}
                        disabled={orphanDeleting}
                        data-testid="orphan-cancel-btn"
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {orphanResult.orphan_count === 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOrphanResult(null)}
                  data-testid="orphan-reset-btn"
                >
                  Vorschau zurücksetzen
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhooks Card (Phase 2A — Real-Time) */}
      <Card data-testid="mindbody-webhooks-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" /> Real-Time Webhooks
          </CardTitle>
          <CardDescription>
            Phase 2: Mindbody pusht Änderungen sofort (statt 15-Min-Polling).
            Empfohlen sobald die Phase-1-Verbindung läuft.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {webhooks.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              Noch keine Webhooks registriert — der Cron läuft weiterhin alle 15 Min.
            </div>
          ) : (
            <div className="space-y-2">
              {webhooks.map((w) => (
                <div
                  key={w.subscription_id}
                  className="flex items-start justify-between gap-3 rounded border p-3 bg-white"
                  data-testid={`mindbody-webhook-${w.subscription_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="h-4 w-4 text-yellow-500" />
                      <code className="text-xs font-mono break-all">{w.subscription_id}</code>
                      <Badge variant="secondary" className="text-xs">{w.status || 'active'}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground break-all">
                      <strong>URL:</strong> {w.webhook_url}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      <strong>Events:</strong> {(w.event_ids || []).join(', ')}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {String(w.status || '').toLowerCase() !== 'active' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => activateWebhook(w.subscription_id)}
                        data-testid={`mindbody-webhook-activate-${w.subscription_id}`}
                      >
                        <Zap className="h-4 w-4 mr-1 text-yellow-500" />
                        Aktivieren
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteWebhook(w.subscription_id)}
                      data-testid={`mindbody-webhook-delete-${w.subscription_id}`}
                    >
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {settings?.last_webhook_at && (
            <div className="text-xs text-muted-foreground">
              Letzter Webhook-Ping: {new Date(settings.last_webhook_at).toLocaleString('de-CH')}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={registerWebhook}
              disabled={registeringWebhook || !settings?.api_key_set}
              data-testid="mindbody-webhook-register-btn"
            >
              {registeringWebhook ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <Webhook className="h-4 w-4 mr-2" />}
              Real-Time-Webhook registrieren
            </Button>
            <Button
              variant="outline"
              onClick={repairWebhooks}
              disabled={repairingWebhooks || !settings?.api_key_set}
              data-testid="mindbody-webhook-repair-btn"
            >
              {repairingWebhooks ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Status prüfen &amp; reparieren
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Staff-Mapping Card — Mitarbeiter-Zuordnung */}
      <Card data-testid="mindbody-staff-mapping-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" /> Mitarbeiter-Zuordnung
          </CardTitle>
          <CardDescription>
            Ordne jeden Mindbody-Mitarbeiter einem StrongFit-Trainer zu.
            Nur zugeordnete Slots erscheinen als „BELEGT"-Block im Kalender —
            unzugeordnete bleiben unsichtbar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {staffMappings.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              Noch keine Mindbody-Mitarbeiter erkannt — taucht automatisch auf,
              sobald der erste Sync Termine liefert.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-3 text-xs uppercase tracking-wider text-muted-foreground border-b pb-2">
                <div className="col-span-5">Mindbody-Mitarbeiter</div>
                <div className="col-span-5">StrongFit-Trainer</div>
                <div className="col-span-2 text-right">Slots</div>
              </div>
              {staffMappings.map((row) => (
                <div
                  key={row.mindbody_staff_id}
                  className="grid grid-cols-12 gap-3 items-center text-sm"
                  data-testid={`staff-mapping-row-${row.mindbody_staff_id}`}
                >
                  <div className="col-span-5 min-w-0">
                    <div className="font-medium truncate">{row.mindbody_staff_name}</div>
                    <div className="text-xs text-muted-foreground">ID: {row.mindbody_staff_id}</div>
                  </div>
                  <div className="col-span-5">
                    <Select
                      value={row.strongfit_trainer_id || 'none'}
                      onValueChange={(value) =>
                        setStaffMapping(row.mindbody_staff_id, value === 'none' ? null : value)
                      }
                    >
                      <SelectTrigger data-testid={`staff-mapping-select-${row.mindbody_staff_id}`}>
                        <SelectValue placeholder="Nicht zugeordnet" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nicht zugeordnet —</SelectItem>
                        {availableTrainers.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name || t.id}{!t.active && ' (inaktiv)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 text-right">
                    <Badge variant={row.strongfit_trainer_id ? 'default' : 'secondary'}>
                      {row.slot_count}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service-Mapping Card — Dienstleistungs-Zuordnung (06/2026) */}
      <Card data-testid="mindbody-service-mapping-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" /> Service-Zuordnung
          </CardTitle>
          <CardDescription>
            Ordne jede StrongFit-Dienstleistung einem Mindbody-Session-Type zu.
            Gepushte Termine erscheinen in Mindbody dann als richtiger Service
            (z.B. „EMS 1:3") statt generisch als „Reserviert". Ohne Zuordnung
            wird der Standard-Session-Type aus den Push-Einstellungen verwendet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessionTypesError && (
            <div className="mb-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-700">
              ⚠️ Mindbody-Session-Types konnten nicht geladen werden: {sessionTypesError}
            </div>
          )}
          {serviceMappings.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              Keine aktiven StrongFit-Services gefunden.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-3 text-xs uppercase tracking-wider text-muted-foreground border-b pb-2">
                <div className="col-span-5">StrongFit-Dienstleistung</div>
                <div className="col-span-5">Mindbody-Session-Type</div>
                <div className="col-span-2 text-right">Push</div>
              </div>
              {serviceMappings.map((row) => (
                <div
                  key={row.strongfit_service_id}
                  className="grid grid-cols-12 gap-3 items-center text-sm"
                  data-testid={`service-mapping-row-${row.strongfit_service_id}`}
                >
                  <div className="col-span-5 min-w-0">
                    <div className="font-medium truncate">{row.strongfit_service_name}</div>
                    <div className="text-xs text-muted-foreground">
                      1:{row.max_participants}{row.duration_minutes ? ` · ${row.duration_minutes} Min` : ''}
                    </div>
                  </div>
                  <div className="col-span-5">
                    <Select
                      value={row.mindbody_session_type_id || 'none'}
                      onValueChange={(value) =>
                        setServiceMapping(row.strongfit_service_id, value === 'none' ? null : value)
                      }
                    >
                      <SelectTrigger data-testid={`service-mapping-select-${row.strongfit_service_id}`}>
                        <SelectValue placeholder="Standard (Reserviert)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Standard (Reserviert) —</SelectItem>
                        {sessionTypes.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name || t.id}
                          </SelectItem>
                        ))}
                        {/* Gemappter Type nicht (mehr) in der Liste → trotzdem anzeigen */}
                        {row.mindbody_session_type_id &&
                          !sessionTypes.some((t) => t.id === row.mindbody_session_type_id) && (
                          <SelectItem value={row.mindbody_session_type_id}>
                            {row.mindbody_session_type_name || row.mindbody_session_type_id}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 text-right">
                    <Badge variant={row.push_to_mindbody ? 'default' : 'secondary'}>
                      {row.push_to_mindbody ? 'aktiv' : 'aus'}
                    </Badge>
                  </div>
                </div>
              ))}
              <p className="text-xs text-muted-foreground pt-2">
                „Push aus" = Service wird nicht zu Mindbody gespiegelt
                (einstellbar pro Service unter Dienstleistungen).
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Credentials Card */}
      <Card data-testid="mindbody-credentials-card">
        <CardHeader>
          <CardTitle>Zugangsdaten</CardTitle>
          <CardDescription>
            Die Anmeldedaten werden verschlüsselt in der StrongFit-Datenbank abgelegt
            (nicht in .env). Felder leer lassen, um den bestehenden Wert zu behalten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="site_id">Site ID</Label>
            <Input
              id="site_id"
              type="number"
              value={form.site_id}
              onChange={(e) => setForm({ ...form, site_id: e.target.value })}
              placeholder="z.B. 123456"
              data-testid="mindbody-site-id-input"
            />
          </div>
          <div>
            <Label htmlFor="api_key">
              API Key {settings?.api_key_set && <Badge variant="secondary" className="ml-2 text-xs">gespeichert</Badge>}
            </Label>
            <Input
              id="api_key"
              type="password"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              placeholder={settings?.api_key_set ? '••• (leer lassen um zu behalten)' : 'Mindbody API Key'}
              data-testid="mindbody-api-key-input"
            />
          </div>
          <div>
            <Label htmlFor="source_name">Source Name</Label>
            <Input
              id="source_name"
              value={form.source_name}
              onChange={(e) => setForm({ ...form, source_name: e.target.value })}
              placeholder="Mindbody Source Name (ohne führenden Unterstrich)"
              data-testid="mindbody-source-name-input"
            />
          </div>
          <div>
            <Label htmlFor="source_password">
              Source Password {settings?.source_password_set && <Badge variant="secondary" className="ml-2 text-xs">gespeichert</Badge>}
            </Label>
            <Input
              id="source_password"
              type="password"
              value={form.source_password}
              onChange={(e) => setForm({ ...form, source_password: e.target.value })}
              placeholder={settings?.source_password_set ? '••• (leer lassen um zu behalten)' : 'Mindbody Source Password'}
              data-testid="mindbody-source-password-input"
            />
          </div>

          <Separator />

          {/* Sync-Richtung (Phase 2 — 4 Modi für Step-by-Step-Tests) */}
          <div className="rounded-lg border p-4 bg-yellow-50/50 space-y-3">
            <div>
              <Label className="text-base font-semibold">Synchronisations-Richtung</Label>
              <p className="text-sm text-muted-foreground">
                Wähle, in welche Richtung Termine fliessen. So kannst du jeden Pfad
                einzeln testen, bevor du auf Vollintegration umstellst.
              </p>
            </div>
            <RadioGroup
              value={form.sync_direction}
              onValueChange={changeDirection}
              data-testid="mindbody-direction-radio"
            >
              <div className="flex items-start gap-2 p-2 rounded hover:bg-yellow-100/50">
                <RadioGroupItem value="off" id="dir-off" data-testid="dir-radio-off" />
                <label htmlFor="dir-off" className="text-sm cursor-pointer flex-1">
                  <span className="font-medium">🔴 Aus</span>
                  <span className="block text-xs text-muted-foreground">
                    Kein Sync. Gespiegelte Mindbody-Slots werden gelöscht. Push-Termine
                    in Mindbody bleiben (manuell aufräumen).
                  </span>
                </label>
              </div>
              <div className="flex items-start gap-2 p-2 rounded hover:bg-yellow-100/50">
                <RadioGroupItem value="mb_to_sf" id="dir-mb-sf" data-testid="dir-radio-mb-sf" />
                <label htmlFor="dir-mb-sf" className="text-sm cursor-pointer flex-1">
                  <span className="font-medium">🟢 Mindbody → StrongFit</span>
                  <span className="block text-xs text-muted-foreground">
                    Read-Only Mirror: Mindbody-Termine erscheinen als „BELEGT" im
                    StrongFit-Kalender (Phase 1).
                  </span>
                </label>
              </div>
              <div className="flex items-start gap-2 p-2 rounded hover:bg-yellow-100/50">
                <RadioGroupItem value="sf_to_mb" id="dir-sf-mb" data-testid="dir-radio-sf-mb" />
                <label htmlFor="dir-sf-mb" className="text-sm cursor-pointer flex-1">
                  <span className="font-medium">🟢 StrongFit → Mindbody</span>
                  <span className="block text-xs text-muted-foreground">
                    Push: StrongFit-Termine (mit „Mindbody-Push"-Flag im Service)
                    werden als „Reserviert" zu Mindbody gepusht.
                  </span>
                </label>
              </div>
              <div className="flex items-start gap-2 p-2 rounded hover:bg-yellow-100/50">
                <RadioGroupItem value="both" id="dir-both" data-testid="dir-radio-both" />
                <label htmlFor="dir-both" className="text-sm cursor-pointer flex-1">
                  <span className="font-medium">🔵 Beide Richtungen</span>
                  <span className="block text-xs text-muted-foreground">
                    Vollintegration: Mirror + Push gleichzeitig aktiv.
                  </span>
                </label>
              </div>
            </RadioGroup>
          </div>

          {/* Push-Konfiguration (nur sichtbar wenn Push aktiv) */}
          {(form.sync_direction === 'sf_to_mb' || form.sync_direction === 'both') && (
            <div className="rounded-lg border p-4 bg-blue-50/50 space-y-3">
              <div>
                <Label className="text-base font-semibold">Push-Konfiguration</Label>
                <p className="text-sm text-muted-foreground">
                  Diese Daten brauchst du, damit StrongFit-Termine als „Reserviert"
                  in Mindbody auftauchen.
                </p>
              </div>
              <div>
                <Label htmlFor="push_session_type_id">Mindbody Session-Type-ID („Reserviert")</Label>
                <Input
                  id="push_session_type_id"
                  type="number"
                  value={form.push_session_type_id}
                  onChange={(e) => setForm({ ...form, push_session_type_id: e.target.value })}
                  placeholder="z.B. 250"
                  data-testid="mindbody-push-session-type-id"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Lege in Mindbody einen Session-Type „Reserviert" o.ä. an und trage
                  die ID hier ein.
                </p>
              </div>
              <div>
                <Label htmlFor="push_default_client_id">Mindbody Default-Client-ID</Label>
                <Input
                  id="push_default_client_id"
                  value={form.push_default_client_id}
                  onChange={(e) => setForm({ ...form, push_default_client_id: e.target.value })}
                  placeholder="z.B. 100012345"
                  data-testid="mindbody-push-default-client-id"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Ein Dummy-Mindbody-Client (z.B. „StrongFit Sync"), unter dem die
                  Push-Termine angelegt werden.
                </p>
              </div>

              {/* Zusatz-Dummy-Clients (06/2026): KEINE echten Mitglieder in MB
                  anlegen — Pool für parallele 1:2/1:3-Gruppentermine */}
              <div className="rounded-lg border bg-white p-3 space-y-3" data-testid="mindbody-extra-clients-card">
                <div>
                  <Label className="font-semibold">Zusatz-Dummy-Clients für Gruppen (1:2 / 1:3)</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    StrongFit-Mitglieder werden <strong>nicht</strong> als echte Clients in
                    Mindbody angelegt (sonst erhalten sie Mindbody-E-Mails/WhatsApp).
                    Lege stattdessen in Mindbody Dummy-Kunden an (z.B. „StrongFit Sync 2/3")
                    und ordne sie hier zu. Pro parallelem Gruppen-Teilnehmer wird ein
                    eigener Dummy verwendet. Die echten Kundennamen stehen in der Termin-Notiz.
                  </p>
                </div>

                {form.push_extra_clients.length > 0 && (
                  <div className="space-y-1">
                    {form.push_extra_clients.map((c, idx) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between rounded border bg-zinc-50 px-3 py-1.5 text-sm"
                        data-testid={`extra-client-row-${c.id}`}
                      >
                        <span>
                          <span className="font-medium">Dummy {idx + 2}:</span> {c.name || 'Unbenannt'}{' '}
                          <span className="text-muted-foreground">(ID {c.id})</span>
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-red-600 hover:text-red-700"
                          onClick={() => removeExtraClient(c.id)}
                          data-testid={`extra-client-remove-${c.id}`}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchClients(); } }}
                    placeholder="Mindbody-Client suchen (z.B. StrongFit)"
                    data-testid="extra-client-search-input"
                  />
                  <Button
                    variant="outline"
                    onClick={searchClients}
                    disabled={searchingClients}
                    data-testid="extra-client-search-btn"
                  >
                    {searchingClients ? <Loader2 className="animate-spin h-4 w-4" /> : 'Suchen'}
                  </Button>
                </div>

                {clientResults.length > 0 && (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {clientResults.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between rounded border px-3 py-1.5 text-sm hover:bg-zinc-50"
                        data-testid={`client-search-result-${c.id}`}
                      >
                        <span>
                          {c.first_name} {c.last_name}{' '}
                          <span className="text-muted-foreground">
                            (ID {c.id}{c.email ? ` · ${c.email}` : ''})
                          </span>
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7"
                          onClick={() => addExtraClient(c)}
                          data-testid={`client-search-add-${c.id}`}
                        >
                          + Hinzufügen
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <Button
            onClick={() => save()}
            disabled={saving}
            className="w-full"
            data-testid="mindbody-save-btn"
          >
            {saving ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : null}
            Zugangsdaten speichern
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MindbodyIntegrationPage;
